import type { DraftClaim } from '../workflow/types';

type DraftClaimQueue = {
  orderNumber: string;
  kept: DraftClaim | null;
  tail: Promise<void>;
};

export type DraftClaimTurn = {
  orderNumber: string;
  itemsKey: string;
  saveDraft: (draftId: string | null) => Promise<string | null>;
  onKept: (draft: DraftClaim | null) => void;
  /** The draft the workflow restored, which the order's queue starts from. */
  savedDraft: DraftClaim | null;
  signal?: AbortSignal;
  /**
   * Use a kept draft whatever items it was saved with: submit writes the final
   * cart itself, and saving first would drop a recorded third-party return.
   */
  keepSavedItems?: boolean;
};

/**
 * Module-wide, so every save and every read of the draft takes its turn in one
 * line. A save for other items landing between another reader's save and its
 * read would hand that reader the methods for the wrong cart, and two saves
 * racing to create the first draft would leave reads and submit on different
 * claims. The kept draft lives here too because a turn must see the result of
 * the turn before it, which a render does not deliver in time.
 */
let queue: DraftClaimQueue | null = null;

const queueFor = ({
  orderNumber,
  savedDraft,
}: DraftClaimTurn): DraftClaimQueue => {
  if (queue?.orderNumber !== orderNumber) {
    queue = { orderNumber, kept: savedDraft, tail: Promise.resolve() };
  }
  return queue;
};

export const resetDraftClaimQueue = (): void => {
  queue = null;
};

const keep = (
  current: DraftClaimQueue,
  draft: DraftClaim | null,
  onKept: DraftClaimTurn['onKept'],
): void => {
  if (current.kept === draft) return;
  current.kept = draft;
  if (queue === current) onKept(draft);
};

const claimIdFor = async (
  current: DraftClaimQueue,
  { itemsKey, saveDraft, onKept, keepSavedItems }: DraftClaimTurn,
): Promise<string | null> => {
  const { kept } = current;
  if (kept && (keepSavedItems || kept.itemsKey === itemsKey)) return kept.id;

  // A failed save keeps the draft: pdp-graphql already replaces one that is
  // gone or no longer pending, so what reaches here is transient, and
  // forgetting the draft would orphan it with any return it recorded.
  const id = await saveDraft(kept?.id ?? null);
  keep(
    current,
    id ? { orderNumber: current.orderNumber, id, itemsKey } : null,
    onKept,
  );
  return id;
};

/**
 * Waits for the order's earlier turns, saves the turn's items to the draft
 * when they differ from what it holds, then runs `work` with the claim id
 * before the next turn starts. A turn whose query was cancelled while it
 * waited saves nothing: the cart it carries is no longer the shopper's.
 */
export const withDraftClaim = <T>(
  turn: DraftClaimTurn,
  work: (claimId: string | null) => Promise<T>,
): Promise<T> => {
  const current = queueFor(turn);
  const run = current.tail.then(async () => {
    turn.signal?.throwIfAborted();
    return work(await claimIdFor(current, turn));
  });
  current.tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};
